import { useState } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2, Warehouse, KeyRound } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { roleBadgeKey } from "@/constants/screens";
import { can } from "@/constants/adminTabs";
import { API, ax, errorText, MARKETPLACES } from "./common";
import WarehouseAssignmentsDialog from "./WarehouseAssignmentsDialog";
import ResetPasswordDialog from "./ResetPasswordDialog";

const INTERNAL_ROLES = ["WAREHOUSE_STAFF"];
const TENANT_ROLES = ["TENANT_ADMIN", "TENANT_MEMBER"];

export default function UsersTab({ users, reload, currentUser, requestDelete, showMsg }) {
  const { t } = useLang();
  const platform = currentUser?.role === "TOOLS_USER";
  const assignable = platform ? INTERNAL_ROLES : TENANT_ROLES;
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(null);
  const [assignmentsFor, setAssignmentsFor] = useState(null);
  const [resetFor, setResetFor] = useState(null);

  // Platform admins manage internal staff only; tenant users belong to their tenant administrators.
  const manageable = (u) => (platform ? INTERNAL_ROLES.includes(u.role) : u.id !== currentUser?.id);

  const openCreate = () => { setEditing(null); setForm({ email: "", password: "", name: "", role: assignable[0], capabilities: [] }); };
  const openEdit = (u) => { setEditing(u); setForm({ email: u.email, password: "", name: u.name, role: u.role, capabilities: u.capabilities || [] }); };

  // Legacy role names still map to a tenant role, so the edited user's own tenant role decides while the role is unchanged.
  const formTenantRole = !form || platform ? null
    : (editing && form.role === editing.role ? editing.tenant_role : form.role);
  const isMemberForm = formTenantRole === "TENANT_MEMBER";
  const isAdminForm = formTenantRole === "TENANT_ADMIN";
  // Options come from the tenant's purchased marketplaces; the platform administrator decides those.
  const entitledMarketplaces = currentUser?.membership?.marketplaces || [];
  const capabilityOptions = ["PLANNING", ...MARKETPLACES.map(k => `MARKETPLACE_${k}`)];
  const capabilityEntitled = (c) => c === "PLANNING" || entitledMarketplaces.includes(c.replace("MARKETPLACE_", ""));
  const capabilityLocked = (c) => isAdminForm || !capabilityEntitled(c);
  const capabilityLabel = (c) => (c === "PLANNING" ? t("adm.cap.PLANNING")
    : `${t("adm.cap.marketplace")}: ${t(`adm.marketplace.${c.replace("MARKETPLACE_", "")}`)}`);
  const toggleCapability = (c, on) => setForm(f => ({
    ...f, capabilities: on ? [...f.capabilities, c] : f.capabilities.filter(x => x !== c),
  }));

  const save = async () => {
    try {
      if (editing) {
        const payload = { name: form.name };
        if (form.email !== editing.email) payload.email = form.email;
        if (form.role !== editing.role) payload.role = form.role;
        if (form.password) payload.password = form.password;
        if (isMemberForm) payload.capabilities = form.capabilities;
        await axios.put(`${API}/users/${editing.id}`, payload, ax);
        showMsg("success", t("adm.us.updated"));
      } else {
        await axios.post(`${API}/users`, { ...form, capabilities: isMemberForm ? form.capabilities : [] }, ax);
        showMsg("success", t("adm.us.created"));
      }
      setForm(null);
      reload();
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
  };

  const roleOptions = editing && !assignable.includes(editing.role) ? [editing.role, ...assignable] : assignable;
  const roleLabel = (r) => t(roleBadgeKey(r));

  return (
    <div>
      <div className="flex justify-end mb-3">
        <Button size="sm" onClick={openCreate} className="gap-1.5 bg-blue-600 hover:bg-blue-700" data-testid="create-user-button">
          <Plus className="w-4 h-4" /> {t(platform ? "adm.us.create_staff" : "admin.create_user")}
        </Button>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden divide-y divide-slate-50">
        {users.map(u => (
          <div key={u.id} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-900 text-sm">{u.name}</p>
              <p className="text-xs text-slate-500">{u.email}</p>
              {platform && u.tenant_name && (
                <p className="text-xs text-slate-500">{t("adm.us.tenant")}: {u.tenant_name} <span className="font-mono text-slate-400">{u.tenant_code}</span></p>
              )}
            </div>
            <span className="text-xs font-medium bg-slate-100 text-slate-700 px-2 py-1 rounded-lg">{roleLabel(u.role)}</span>
            {platform && u.tenant_role === "TENANT_ADMIN" && (
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600"
                onClick={() => setResetFor(u)} aria-label={t("adm.us.reset_password")}>
                <KeyRound className="w-3.5 h-3.5" />
              </Button>
            )}
            {manageable(u) && (
              <div className="flex gap-1">
                {platform && can(currentUser, "warehouse_assignments:manage") && (
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600"
                    onClick={() => setAssignmentsFor(u)} aria-label={t("adm.us.warehouses_title")}>
                    <Warehouse className="w-3.5 h-3.5" />
                  </Button>
                )}
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600" onClick={() => openEdit(u)} aria-label={t("common.edit")}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                  onClick={() => requestDelete({ type: "user", id: u.id, name: u.name })} aria-label={t("common.delete")}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={!!form} onOpenChange={(open) => !open && setForm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{editing ? t("adm.us.edit") : t(platform ? "adm.us.create_staff" : "admin.create_user")}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>{t("admin.email")}</Label>
                <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="h-11" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("common.name")}</Label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="h-11" />
              </div>
              {roleOptions.length > 1 && (
                <div className="space-y-1.5">
                  <Label>{t("admin.role")}</Label>
                  <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
                    <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {roleOptions.map(r => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1.5">
                <Label>{t("admin.password")}{editing ? ` (${t("adm.us.keep_password")})` : " *"}</Label>
                <Input type="password" autoComplete="new-password" value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))} className="h-11" />
              </div>
              {(isMemberForm || isAdminForm) && (
                <div className="space-y-2">
                  <Label>{t("adm.cap.title")}</Label>
                  {capabilityOptions.map(c => (
                    <label key={c} className="flex items-center gap-2 text-sm text-slate-700">
                      <Checkbox checked={isAdminForm ? capabilityEntitled(c) : form.capabilities.includes(c)} disabled={capabilityLocked(c)}
                        onCheckedChange={checked => toggleCapability(c, checked === true)} />
                      {capabilityLabel(c)}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>{t("common.cancel")}</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-700">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ResetPasswordDialog admin={resetFor} tenantId={resetFor?.tenant_id} onClose={() => setResetFor(null)} showMsg={showMsg} />
      <WarehouseAssignmentsDialog user={assignmentsFor} onClose={() => setAssignmentsFor(null)} showMsg={showMsg} />
    </div>
  );
}
